import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap as useLeafletMap } from 'react-leaflet';
import { useMap } from '../../contexts/MapContext';
import { useData } from '../../contexts/DataContext';
import ZoneLayer from './ZoneLayer';
import './styles.css';

// Component to handle map events and sync
const MapEventHandler = ({ viewId }) => {
  const leafletMap = useLeafletMap();
  const { registerMap, syncMaps, isCompareMode, viewState } = useMap();
  const isSyncingRef = useRef(false);

  // Register map instance
  useEffect(() => {
    registerMap(viewId, leafletMap);
    return () => registerMap(viewId, null);
  }, [viewId, leafletMap, registerMap]);

  // Handle move/zoom events for sync
  useEffect(() => {
    const handleMoveEnd = () => {
      if (!isCompareMode || isSyncingRef.current) return;

      const center = leafletMap.getCenter();
      const zoom = leafletMap.getZoom();
      syncMaps(viewId, [center.lat, center.lng], zoom);
    };

    leafletMap.on('moveend', handleMoveEnd);
    leafletMap.on('zoomend', handleMoveEnd);

    return () => {
      leafletMap.off('moveend', handleMoveEnd);
      leafletMap.off('zoomend', handleMoveEnd);
    };
  }, [leafletMap, viewId, syncMaps, isCompareMode]);

  // Sync to external view state changes
  useEffect(() => {
    if (!isCompareMode) return;

    const currentCenter = leafletMap.getCenter();
    const currentZoom = leafletMap.getZoom();

    const centerChanged =
      Math.abs(currentCenter.lat - viewState.center[0]) > 0.0001 ||
      Math.abs(currentCenter.lng - viewState.center[1]) > 0.0001;
    const zoomChanged = currentZoom !== viewState.zoom;

    if (centerChanged || zoomChanged) {
      isSyncingRef.current = true;
      leafletMap.setView(viewState.center, viewState.zoom, { animate: false });
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 100);
    }
  }, [leafletMap, viewState, isCompareMode]);

  return null;
};

const MapView = ({ viewId = 1 }) => {
  const { clientConfig } = useData();
  const mapConfig = clientConfig.map || { center: [45.62, 9.0], zoom: 10, minZoom: 8, maxZoom: 18 };

  return (
    <div className="map-view-container">
      <MapContainer
        center={mapConfig.center}
        zoom={mapConfig.zoom}
        minZoom={mapConfig.minZoom}
        maxZoom={mapConfig.maxZoom}
        className="map-container"
        zoomControl={false}
        attributionControl={true}
      >
                <TileLayer
          url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`}
          maxZoom={20}
        />

        {/* Zoom control in bottom-right */}
        <ZoomControl position="bottomright" />

        {/* Dark tile layer - CartoDB Dark Matter */}

        {/* Map event handler for sync */}
        <MapEventHandler viewId={viewId} />

        {/* GeoJSON zone layer */}
        <ZoneLayer viewId={viewId} />
      </MapContainer>
    </div>
  );
};

export default MapView;
